import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFilter,
  FaHeadset,
  FaMagic,
  FaSpinner,
  FaSyncAlt,
  FaTags,
  FaTimes,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import {
  createHelpDocJob,
  fetchCategoryDetail,
  fetchOverview,
  fetchTaxonomy,
  fetchTopics,
} from "./api";

// ── Constants ─────────────────────────────────────────────────────────────────

const ACCENT = "#7F7FD5";

// Stable palette — order is fixed so categories keep the same color across
// reloads. We sort categories by `sortOrder`/total before mapping into this.
const CATEGORY_PALETTE = [
  "#7F7FD5",
  "#E786D7",
  "#86B7E7",
  "#E7A686",
  "#86E7A6",
  "#E7E786",
  "#A686E7",
  "#67C3E7",
  "#E76767",
  "#67E7C3",
  "#E76FB1",
  "#9CB3FF",
];

const TAG_NAMESPACE_COLORS = {
  vendor: "#86B7E7",
  auth_provider: "#E786D7",
  surface: "#86E7A6",
  outcome: "#E7A686",
};

const WINDOW_OPTIONS = [
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 180, label: "180d" },
  { days: 365, label: "1y" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const formatBucketLabel = (key, bucket) => {
  if (!key) return "";
  if (bucket === "monthly") {
    // YYYY-MM → "Jan 2026"
    const [y, m] = key.split("-");
    if (!y || !m) return key;
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  // YYYY-MM-DD → "Mar 3"
  const d = new Date(key + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const getJiraIssueUrl = (jiraKey) =>
  jiraKey ? `https://addyai.atlassian.net/browse/${jiraKey}` : null;

// Special-case humanizations where the auto title-casing isn't right.
// Anything not in this map falls back to title-casing (snake_case → Snake Case).
const TAG_LABEL_OVERRIDES = {
  // outcome
  "outcome:how_to": "How-To",
  "outcome:bug": "Bug",
  "outcome:feature_request": "Feature Request",
  "outcome:account_op": "Account Op",
  // vendor — preserve brand casing
  "vendor:arive": "Arive",
  "vendor:encompass": "Encompass",
  "vendor:meridianlink": "MeridianLink",
  "vendor:lendingpad": "LendingPad",
  "vendor:finmo": "Finmo",
  "vendor:blend": "Blend",
  "vendor:google_drive": "Google Drive",
  "vendor:fannie_mae": "Fannie Mae",
  // auth_provider
  "auth_provider:microsoft": "Microsoft",
  "auth_provider:google": "Google",
  "auth_provider:apple": "Apple",
  "auth_provider:email_password": "Email + Password",
  // surface
  "surface:chrome_extension": "Chrome",
  "surface:edge_extension": "Edge",
  "surface:web_app": "Web App",
  "surface:mobile": "Mobile",
  "surface:gmail": "Gmail",
  "surface:outlook": "Outlook",
};

const NAMESPACE_LABELS = {
  vendor: "Vendor",
  auth_provider: "Auth",
  surface: "Surface",
  outcome: "Outcome",
};

// Accepts a raw tag like "vendor:arive" or "outcome:how_to" and returns
// { label, namespace, namespaceLabel, color, raw }. Used everywhere a tag
// is rendered so the UI never shows snake_case to the user.
const formatTag = (raw) => {
  const safe = (raw || "").toString();
  const [ns, ...rest] = safe.split(":");
  const namespace = rest.length ? ns : "";
  const value = rest.length ? rest.join(":") : ns;

  const override = TAG_LABEL_OVERRIDES[safe];
  const label =
    override ||
    (value || "")
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ") ||
    "—";

  return {
    raw: safe,
    label,
    namespace,
    namespaceLabel: NAMESPACE_LABELS[namespace] || namespace,
    color: TAG_NAMESPACE_COLORS[namespace] || ACCENT,
  };
};

// Shorter version of a category name for chart legends where space is tight.
// "Product Features and Training" → "Product Features"
// "LOS Document Sync Issues" → "LOS Document Sync"
const shortCategoryName = (name) => {
  if (!name) return "";
  const trimmed = name.replace(/\s+(and|or|&|of)\s+/gi, " ");
  const words = trimmed.split(" ");
  if (words.length <= 3) return trimmed;
  return words.slice(0, 3).join(" ");
};

// Convert the API's parallel-arrays trend payload into Recharts-friendly rows.
// Input: buckets:[k1,k2], series:{ catId: [3,4] }
// Output: [{bucket:"k1", catA:3, catB:1}, {bucket:"k2", catA:4, catB:0}]
const seriesToRechartsRows = (buckets, series) => {
  const ids = Object.keys(series || {});
  return (buckets || []).map((b, i) => {
    const row = { bucket: b };
    for (const id of ids) {
      row[id] = series[id]?.[i] ?? 0;
    }
    return row;
  });
};

// Pick top-N categories by total volume so the chart isn't a 12-color mess.
const pickTopCategoryIds = (categories, n = 6) =>
  [...categories]
    .sort((a, b) => (b.total || 0) - (a.total || 0))
    .slice(0, n)
    .map((c) => c.categoryId);

// ── Sub-components ────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sublabel, accent = ACCENT, isDark }) => {
  // Auto-size the value font: numeric-looking values stay big and bold;
  // longer text values shrink so they don't crowd the card.
  const valueStr = value == null ? "" : String(value);
  const isNumericLike = /^[\d.,]+$/.test(valueStr);
  const valueFontSize = isNumericLike
    ? 28
    : valueStr.length > 22
      ? 16
      : valueStr.length > 14
        ? 20
        : 24;

  return (
    <div
      style={{
        flex: "1 1 200px",
        minWidth: 200,
        background: isDark ? "#161b2e" : "#fff",
        border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
        borderRadius: 12,
        padding: "18px 20px",
        boxShadow: isDark
          ? "0 4px 24px rgba(0,0,0,0.3)"
          : "0 2px 12px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: isDark ? "#a0aec0" : "#6b7280",
          marginBottom: 8,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: valueFontSize,
          fontWeight: 700,
          color: accent,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
        title={valueStr}
      >
        {value}
      </div>
      {sublabel && (
        <div
          style={{
            fontSize: 12,
            color: isDark ? "#a0aec0" : "#6b7280",
            marginTop: 6,
          }}
        >
          {sublabel}
        </div>
      )}
    </div>
  );
};


const TagPill = ({ tag, count, onClick, isDark, active, showNamespace = true }) => {
  const t = formatTag(tag);
  return (
    <button
      type="button"
      onClick={onClick}
      title={t.raw}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px 4px 6px",
        borderRadius: 999,
        border: `1px solid ${active ? t.color : isDark ? "#3d4466" : "#e2e8f0"}`,
        background: active
          ? t.color + "22"
          : isDark
            ? "#1e2130"
            : "#f7f9fc",
        color: isDark ? "#e2e8f0" : "#1a202c",
        fontSize: 12,
        fontWeight: 500,
        cursor: onClick ? "pointer" : "default",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
      }}
    >
      {showNamespace && t.namespaceLabel && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            padding: "2px 6px",
            borderRadius: 999,
            background: t.color + "33",
            color: t.color,
          }}
        >
          {t.namespaceLabel}
        </span>
      )}
      <span>{t.label}</span>
      {typeof count === "number" && (
        <span style={{ color: isDark ? "#a0aec0" : "#6b7280" }}>· {count}</span>
      )}
    </button>
  );
};

const TopicsTable = ({
  categories,
  totalActionable,
  selectedId,
  onSelect,
  isDark,
  colorMap,
}) => {
  const dividerColor = isDark ? "#2d3148" : "#e2e8f0";
  const total = totalActionable || 1;
  const max = Math.max(1, ...categories.map((c) => c.totalInWindow || 0));

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          fontSize: 14,
        }}
      >
        <thead>
          <tr
            style={{
              textAlign: "left",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: isDark ? "#a0aec0" : "#6b7280",
            }}
          >
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}` }}>
              Category
            </th>
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}`, textAlign: "right" }}>
              In window
            </th>
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}`, textAlign: "right" }}>
              All-time
            </th>
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}`, width: "30%" }}>
              Share
            </th>
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}` }}>
              Top tags
            </th>
            <th style={{ padding: "10px 12px", borderBottom: `1px solid ${dividerColor}`, width: 24 }} />
          </tr>
        </thead>
        <tbody>
          {categories.map((c) => {
            const isSelected = c.categoryId === selectedId;
            const pctInWindow = ((c.totalInWindow / total) * 100).toFixed(1);
            const barWidth = Math.round((c.totalInWindow / max) * 100);
            const color = colorMap[c.categoryId] || ACCENT;
            return (
              <tr
                key={c.categoryId}
                onClick={() => onSelect(c.categoryId)}
                style={{
                  cursor: "pointer",
                  background: isSelected
                    ? isDark
                      ? "#1e2342"
                      : "#f3f4ff"
                    : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = isDark ? "#1a1e35" : "#f9fafb";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "transparent";
                }}
              >
                <td
                  style={{
                    padding: "12px 12px",
                    borderBottom: `1px solid ${dividerColor}`,
                    fontWeight: 600,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div>{c.name}</div>
                      {c.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: isDark ? "#a0aec0" : "#6b7280",
                            fontWeight: 400,
                            marginTop: 2,
                            maxWidth: 380,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={c.description}
                        >
                          {c.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td
                  style={{
                    padding: "12px 12px",
                    borderBottom: `1px solid ${dividerColor}`,
                    textAlign: "right",
                    fontWeight: 600,
                  }}
                >
                  {c.totalInWindow}
                </td>
                <td
                  style={{
                    padding: "12px 12px",
                    borderBottom: `1px solid ${dividerColor}`,
                    textAlign: "right",
                    color: isDark ? "#a0aec0" : "#6b7280",
                  }}
                >
                  {c.total}
                </td>
                <td style={{ padding: "12px 12px", borderBottom: `1px solid ${dividerColor}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        flex: 1,
                        height: 6,
                        background: isDark ? "#2d3148" : "#edf2f7",
                        borderRadius: 999,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: "100%",
                          background: color,
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        color: isDark ? "#a0aec0" : "#6b7280",
                        minWidth: 42,
                        textAlign: "right",
                      }}
                    >
                      {pctInWindow}%
                    </span>
                  </div>
                </td>
                <td style={{ padding: "12px 12px", borderBottom: `1px solid ${dividerColor}` }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(c.topTags || []).slice(0, 3).map((t) => (
                      <TagPill
                        key={t.tag}
                        tag={t.tag}
                        count={t.count}
                        isDark={isDark}
                      />
                    ))}
                    {(!c.topTags || c.topTags.length === 0) && (
                      <span style={{ fontSize: 12, color: isDark ? "#a0aec0" : "#6b7280" }}>
                        —
                      </span>
                    )}
                  </div>
                </td>
                <td
                  style={{
                    padding: "12px 12px",
                    borderBottom: `1px solid ${dividerColor}`,
                    color: isDark ? "#a0aec0" : "#6b7280",
                  }}
                >
                  <FaChevronRight />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TrendChart = ({ rows, categoryIds, idToName, colorMap, bucket, isDark }) => {
  const tickFormatter = (k) => formatBucketLabel(k, bucket);

  return (
    <div>
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <AreaChart data={rows} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid
              stroke={isDark ? "#2d3148" : "#edf2f7"}
              vertical={false}
            />
            <XAxis
              dataKey="bucket"
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11, fill: isDark ? "#a0aec0" : "#6b7280" }}
              axisLine={{ stroke: isDark ? "#2d3148" : "#cbd5e0" }}
              tickLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: isDark ? "#a0aec0" : "#6b7280" }}
              axisLine={{ stroke: isDark ? "#2d3148" : "#cbd5e0" }}
              tickLine={false}
              allowDecimals={false}
              width={32}
            />
            <Tooltip
              contentStyle={{
                background: isDark ? "#1e2130" : "#fff",
                border: `1px solid ${isDark ? "#3d4466" : "#e2e8f0"}`,
                borderRadius: 8,
                fontSize: 12,
                color: isDark ? "#e2e8f0" : "#1a202c",
                boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 16px rgba(0,0,0,0.08)",
              }}
              labelFormatter={tickFormatter}
              formatter={(value, key) => [value, idToName[key] || key]}
              itemStyle={{ color: isDark ? "#e2e8f0" : "#1a202c" }}
            />
            {categoryIds.map((id) => (
              <Area
                key={id}
                type="monotone"
                dataKey={id}
                stackId="1"
                stroke={colorMap[id] || ACCENT}
                fill={colorMap[id] || ACCENT}
                fillOpacity={0.6}
                strokeWidth={1.2}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Custom compact legend — one row of pill chips, never wraps awkwardly */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${isDark ? "#2d3148" : "#edf2f7"}`,
        }}
      >
        {categoryIds.map((id) => (
          <div
            key={id}
            title={idToName[id] || id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: isDark ? "#cbd5e0" : "#4a5568",
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: colorMap[id] || ACCENT,
                display: "inline-block",
              }}
            />
            <span>{shortCategoryName(idToName[id] || id)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CategoryDetailView = ({
  detail,
  loading,
  error,
  bucket,
  isDark,
  tagFilter,
  setTagFilter,
  onLoadMore,
  loadingMore,
  categoryColor = ACCENT,
  onBack,
  cardStyle,
}) => {
  const dividerColor = isDark ? "#2d3148" : "#e2e8f0";
  const mutedColor = isDark ? "#a0aec0" : "#6b7280";
  const subtleBg = isDark ? "#1a1f33" : "#fafbfd";

  const [generatingArticle, setGeneratingArticle] = useState(false);

  // Kicks off a help-center article job for this category (optionally scoped to
  // the active tag filter). A human asking is signal enough — no threshold.
  const handleGenerateArticle = async () => {
    if (!detail?.category) return;
    const topicPhrase = tagFilter
      ? `${detail.category.name} — ${formatTag(tagFilter).label}`
      : detail.category.name;
    setGeneratingArticle(true);
    try {
      const resp = await createHelpDocJob({
        topicPhrase,
        categoryId: detail.category.id,
      });
      const created = resp?.data?.created;
      toast.success(
        created
          ? `Article job started for “${topicPhrase}”. You'll get a Slack proposal to approve.`
          : `A job for “${topicPhrase}” is already in progress.`,
      );
    } catch (err) {
      toast.error(`Couldn't start article job: ${err?.message || err}`);
    } finally {
      setGeneratingArticle(false);
    }
  };

  // ESC returns to dashboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  const trendRows = (detail?.trend?.buckets || []).map((b, i) => ({
    bucket: b,
    count: detail.trend.counts?.[i] ?? 0,
  }));

  // Inline stat group: typography only, vertical dividers between cells
  const StatGroup = () => (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        flexWrap: "wrap",
      }}
    >
      <InlineStat
        label="In window"
        value={detail.filteredInWindow}
        hint={`last ${detail.timeRange?.days || "—"}d`}
        accent={categoryColor}
        isDark={isDark}
      />
      <div style={{ width: 1, background: dividerColor, margin: "4px 24px" }} />
      <InlineStat
        label="Filtered"
        value={detail.filteredTotal}
        hint={tagFilter ? formatTag(tagFilter).label : "all tags"}
        accent={tagFilter ? "#E76FB1" : categoryColor}
        isDark={isDark}
      />
      <div style={{ width: 1, background: dividerColor, margin: "4px 24px" }} />
      <InlineStat
        label="All-time"
        value={detail.total}
        hint="this category"
        accent={mutedColor}
        isDark={isDark}
      />
    </div>
  );

  return (
    <div style={{ animation: "supportFadeIn 0.2s ease-out" }}>
      {/* Top action bar: back + generate-article */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            fontSize: 13,
            fontWeight: 600,
            border: `1px solid ${dividerColor}`,
            background: isDark ? "#161b2e" : "#fff",
            color: isDark ? "#e2e8f0" : "#1a202c",
            borderRadius: 8,
            cursor: "pointer",
            transition: "background 0.15s ease, border-color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = categoryColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = dividerColor;
          }}
          title="Back (Esc)"
        >
          <FaArrowLeft style={{ fontSize: 12 }} />
          Back to all categories
        </button>

        {detail && (
          <button
            type="button"
            onClick={handleGenerateArticle}
            disabled={generatingArticle}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 700,
              border: "none",
              background: generatingArticle
                ? mutedColor
                : `linear-gradient(135deg, ${categoryColor}, #7F7FD5)`,
              color: "#fff",
              borderRadius: 8,
              cursor: generatingArticle ? "default" : "pointer",
              opacity: generatingArticle ? 0.7 : 1,
              transition: "opacity 0.15s ease, transform 0.15s ease",
            }}
            title={
              tagFilter
                ? `Generate a help article for ${detail.category.name} — ${formatTag(tagFilter).label}`
                : `Generate a help article for ${detail.category.name}`
            }
          >
            {generatingArticle ? (
              <FaSpinner style={{ fontSize: 12, animation: "spin 1s linear infinite" }} />
            ) : (
              <FaMagic style={{ fontSize: 12 }} />
            )}
            {generatingArticle ? "Starting…" : "Generate help article"}
          </button>
        )}
      </div>

      {loading && !detail && (
        <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 10, color: categoryColor }}>
          <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
          <span>Loading category details…</span>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 8,
            background: isDark ? "#3a1d20" : "#fff5f5",
            border: `1px solid ${isDark ? "#e76767" : "#feb2b2"}`,
            color: isDark ? "#feb2b2" : "#c53030",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          <FaExclamationTriangle style={{ marginRight: 8 }} />
          {error}
        </div>
      )}

      {detail && (
        <>
          {/* Hero card with side color stripe + name + description + stats */}
          <div
            style={{
              ...cardStyle,
              padding: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <div style={{ width: 5, background: categoryColor, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: "24px 28px", minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: mutedColor,
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: categoryColor,
                  }}
                />
                Category
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 28,
                  fontWeight: 700,
                  letterSpacing: "-0.015em",
                  lineHeight: 1.2,
                }}
              >
                {detail.category.name}
              </h2>
              {detail.category.description && (
                <p
                  style={{
                    margin: "12px 0 22px",
                    fontSize: 14,
                    color: mutedColor,
                    lineHeight: 1.6,
                    maxWidth: 800,
                  }}
                >
                  {detail.category.description}
                </p>
              )}
              <StatGroup />
            </div>
          </div>

          {/* Trend card — same AreaChart language as main page, in category color */}
          <div style={cardStyle}>
            <DrawerSection
              title="Trend over time"
              hint={
                detail.timeRange
                  ? `Per ${bucket === "monthly" ? "month" : "week"} · last ${detail.timeRange.days}d`
                  : ""
              }
              isDark={isDark}
              lastSection
            >
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={trendRows}
                      margin={{ top: 8, right: 4, left: -16, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="categoryDrawerGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={categoryColor} stopOpacity={0.55} />
                          <stop offset="100%" stopColor={categoryColor} stopOpacity={0.04} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={isDark ? "#2d3148" : "#edf2f7"} vertical={false} />
                      <XAxis
                        dataKey="bucket"
                        tickFormatter={(k) => formatBucketLabel(k, bucket)}
                        tick={{ fontSize: 11, fill: mutedColor }}
                        axisLine={{ stroke: isDark ? "#2d3148" : "#cbd5e0" }}
                        tickLine={false}
                        minTickGap={20}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: mutedColor }}
                        axisLine={{ stroke: isDark ? "#2d3148" : "#cbd5e0" }}
                        tickLine={false}
                        allowDecimals={false}
                        width={32}
                      />
                      <Tooltip
                        contentStyle={{
                          background: isDark ? "#1e2130" : "#fff",
                          border: `1px solid ${isDark ? "#3d4466" : "#e2e8f0"}`,
                          borderRadius: 8,
                          fontSize: 12,
                          color: isDark ? "#e2e8f0" : "#1a202c",
                          boxShadow: isDark
                            ? "0 4px 16px rgba(0,0,0,0.4)"
                            : "0 4px 16px rgba(0,0,0,0.08)",
                        }}
                        labelFormatter={(k) => formatBucketLabel(k, bucket)}
                        formatter={(value) => [value, "tickets"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke={categoryColor}
                        strokeWidth={1.6}
                        fill="url(#categoryDrawerGradient)"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </DrawerSection>
          </div>

          {/* Tags & status card */}
          <div style={cardStyle}>
              <DrawerSection
                title="Tags & status"
                hint="Click a tag to filter tickets below"
                isDark={isDark}
                lastSection
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <MiniLabel label="Top tags" isDark={isDark}>
                      {tagFilter && (
                        <button
                          type="button"
                          onClick={() => setTagFilter(null)}
                          style={{
                            fontSize: 11,
                            border: "none",
                            background: "transparent",
                            color: categoryColor,
                            cursor: "pointer",
                            fontWeight: 600,
                            padding: 0,
                          }}
                        >
                          Clear filter
                        </button>
                      )}
                    </MiniLabel>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {(detail.topTags || []).slice(0, 18).map((t) => (
                        <TagPill
                          key={t.tag}
                          tag={t.tag}
                          count={t.count}
                          isDark={isDark}
                          active={tagFilter === t.tag}
                          onClick={() =>
                            setTagFilter(tagFilter === t.tag ? null : t.tag)
                          }
                        />
                      ))}
                      {(!detail.topTags || detail.topTags.length === 0) && (
                        <span style={{ fontSize: 13, color: mutedColor }}>
                          No tags yet.
                        </span>
                      )}
                    </div>
                  </div>

                  {detail.statusBreakdown?.length > 0 && (
                    <div>
                      <MiniLabel label="Status breakdown" isDark={isDark} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {detail.statusBreakdown.map((s) => (
                          <StatusChip
                            key={s.status}
                            status={s.status}
                            count={s.count}
                            isDark={isDark}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </DrawerSection>
          </div>

          {/* Tickets card — main attraction */}
          <div style={cardStyle}>
              <DrawerSection
                title="Tickets"
                hint={
                  tagFilter
                    ? `${detail.filteredTotal} matching · sorted by created`
                    : `${detail.total} total · sorted by created`
                }
                isDark={isDark}
                lastSection
              >
                {tagFilter && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                      background: subtleBg,
                      border: `1px solid ${dividerColor}`,
                      borderRadius: 999,
                      fontSize: 12,
                      marginBottom: 12,
                      color: mutedColor,
                    }}
                  >
                    <FaFilter style={{ fontSize: 10 }} />
                    Filter:
                    <strong style={{ color: isDark ? "#e2e8f0" : "#1a202c" }}>
                      {formatTag(tagFilter).label}
                    </strong>
                    <button
                      type="button"
                      onClick={() => setTagFilter(null)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: mutedColor,
                        padding: 0,
                        marginLeft: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                      aria-label="Clear filter"
                    >
                      <FaTimes style={{ fontSize: 10 }} />
                    </button>
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(detail.tickets || []).map((t) => (
                    <TicketRow
                      key={t.jiraKey}
                      ticket={t}
                      isDark={isDark}
                      categoryColor={categoryColor}
                    />
                  ))}
                  {(!detail.tickets || detail.tickets.length === 0) && (
                    <div
                      style={{
                        fontSize: 13,
                        color: mutedColor,
                        padding: "20px 0",
                        textAlign: "center",
                      }}
                    >
                      No tickets match this filter.
                    </div>
                  )}
                </div>
                {detail.pagination?.hasMore && (
                  <button
                    type="button"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    style={{
                      marginTop: 14,
                      padding: "10px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      border: `1px solid ${dividerColor}`,
                      background: "transparent",
                      color: isDark ? "#e2e8f0" : "#1a202c",
                      borderRadius: 8,
                      cursor: loadingMore ? "wait" : "pointer",
                      width: "100%",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingMore) e.currentTarget.style.background = subtleBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {loadingMore ? "Loading…" : "Load more tickets"}
                  </button>
                )}
              </DrawerSection>
          </div>
        </>
      )}
    </div>
  );
};

const InlineStat = ({ label, value, hint, accent, isDark }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
    <div
      style={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: isDark ? "#a0aec0" : "#6b7280",
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1.05 }}>
      {value}
    </div>
    {hint && (
      <div
        style={{
          fontSize: 11,
          color: isDark ? "#a0aec0" : "#6b7280",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {hint}
      </div>
    )}
  </div>
);

const DrawerSection = ({ title, hint, isDark, children, lastSection }) => (
  <div
    style={{
      paddingBottom: lastSection ? 0 : 24,
      marginBottom: lastSection ? 0 : 24,
      borderBottom: lastSection ? "none" : `1px solid ${isDark ? "#2d3148" : "#edf2f7"}`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}>
        {title}
      </div>
      {hint && (
        <div style={{ fontSize: 11.5, color: isDark ? "#a0aec0" : "#6b7280" }}>
          {hint}
        </div>
      )}
    </div>
    {children}
  </div>
);

const MiniLabel = ({ label, isDark, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      marginBottom: 8,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: isDark ? "#a0aec0" : "#6b7280",
      fontWeight: 600,
    }}
  >
    <span>{label}</span>
    {children}
  </div>
);

// Status chip with subtle hue based on whether it's open/closed-ish
const StatusChip = ({ status, count, isDark }) => {
  const lowered = (status || "").toLowerCase();
  const isClosed = /done|closed|resolved|completed/.test(lowered);
  const isWaiting = /wait|pending|hold/.test(lowered);
  const accent = isClosed
    ? "#86E7A6"
    : isWaiting
      ? "#E7E786"
      : isDark
        ? "#cbd5e0"
        : "#94a3b8";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        padding: "5px 10px",
        borderRadius: 999,
        background: isDark ? "#1e2130" : "#f7f9fc",
        border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
        color: isDark ? "#e2e8f0" : "#1a202c",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: accent,
          flexShrink: 0,
        }}
      />
      <strong>{status}</strong>
      <span style={{ color: isDark ? "#a0aec0" : "#6b7280", fontWeight: 500 }}>· {count}</span>
    </span>
  );
};

const SectionHeader = ({ title, isDark, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      marginBottom: 10,
    }}
  >
    <div
      style={{
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: isDark ? "#a0aec0" : "#6b7280",
        fontWeight: 600,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const TicketRow = ({ ticket, isDark, categoryColor = ACCENT }) => {
  const dividerColor = isDark ? "#2d3148" : "#edf2f7";
  const mutedColor = isDark ? "#a0aec0" : "#6b7280";
  const url = ticket.url || getJiraIssueUrl(ticket.jiraKey);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        padding: "14px 16px",
        borderRadius: 10,
        border: `1px solid ${dividerColor}`,
        background: isDark ? "#1a1f33" : "#fff",
        transition: "border-color 0.15s ease, transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = categoryColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = dividerColor;
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              color: mutedColor,
              marginBottom: 5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            <span style={{ color: categoryColor }}>{ticket.jiraKey}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>
              {ticket.status}
            </span>
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.4,
              letterSpacing: "-0.005em",
            }}
          >
            {ticket.summary || "(no summary)"}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: mutedColor,
              padding: "3px 8px",
              borderRadius: 999,
              background: isDark ? "#161b2e" : "#f7f9fc",
              border: `1px solid ${dividerColor}`,
              whiteSpace: "nowrap",
            }}
          >
            {formatDate(ticket.created)}
          </span>
          <FaExternalLinkAlt
            style={{ fontSize: 9, color: mutedColor, opacity: 0.6 }}
          />
        </div>
      </div>
      {ticket.classification?.topicPhrase && (
        <div
          style={{
            fontSize: 12,
            color: mutedColor,
            fontStyle: "italic",
            marginBottom: 8,
            paddingLeft: 0,
          }}
        >
          “{ticket.classification.topicPhrase}”
        </div>
      )}
      {ticket.classification?.tags?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {ticket.classification.tags.map((tag) => (
            <TagPill key={tag} tag={tag} isDark={isDark} />
          ))}
        </div>
      )}
    </a>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const SupportInsightsPage = ({ onReady }) => {
  const [theme] = useDarkMode();
  const isDark = theme === "dark";

  const [days, setDays] = useState(90);
  const [bucket, setBucket] = useState("weekly");

  const [overview, setOverview] = useState(null);
  const [topics, setTopics] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Drawer state
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(null);
  const [categoryTagFilter, setCategoryTagFilter] = useState(null);
  const [categoryLoadingMore, setCategoryLoadingMore] = useState(false);

  const requestIdRef = useRef(0);

  // ── Initial / window-change load: overview + topics + taxonomy ──
  const loadDashboard = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const [overviewResp, topicsResp, taxonomyResp] = await Promise.all([
        fetchOverview(days),
        fetchTopics({ bucket, days }),
        taxonomy ? Promise.resolve({ data: taxonomy }) : fetchTaxonomy(),
      ]);
      if (reqId !== requestIdRef.current) return;
      setOverview(overviewResp.data);
      setTopics(topicsResp.data);
      if (!taxonomy && taxonomyResp?.data) setTaxonomy(taxonomyResp.data);
    } catch (err) {
      console.error("[SupportInsights] dashboard load failed", err);
      if (reqId === requestIdRef.current) {
        setError(err?.message || "Failed to load support insights.");
        toast.error(err?.message || "Failed to load support insights.");
      }
    } finally {
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  }, [days, bucket, taxonomy]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  // ── Drawer load when a category is selected or filter/bucket changes ──
  const loadCategory = useCallback(
    async (categoryId, { append = false, tag = categoryTagFilter } = {}) => {
      if (!categoryId) return;
      const reqId = ++requestIdRef.current;
      if (append) setCategoryLoadingMore(true);
      else {
        setCategoryLoading(true);
        setCategoryError(null);
      }
      try {
        const offset = append ? categoryDetail?.tickets?.length || 0 : 0;
        const resp = await fetchCategoryDetail(categoryId, {
          bucket,
          days,
          limit: 25,
          offset,
          tag,
        });
        if (reqId !== requestIdRef.current) return;
        if (append) {
          setCategoryDetail((prev) => ({
            ...resp.data,
            tickets: [
              ...(prev?.tickets || []),
              ...(resp.data.tickets || []),
            ],
          }));
        } else {
          setCategoryDetail(resp.data);
        }
      } catch (err) {
        console.error("[SupportInsights] category load failed", err);
        if (reqId === requestIdRef.current) {
          setCategoryError(err?.message || "Failed to load category details.");
        }
      } finally {
        if (reqId === requestIdRef.current) {
          if (append) setCategoryLoadingMore(false);
          else setCategoryLoading(false);
        }
      }
    },
    [bucket, days, categoryTagFilter, categoryDetail?.tickets?.length],
  );

  useEffect(() => {
    if (!selectedCategoryId) return;
    loadCategory(selectedCategoryId, { tag: categoryTagFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, bucket, days, categoryTagFilter]);

  const handleSelectCategory = (id) => {
    if (id === selectedCategoryId) return;
    setCategoryDetail(null);
    setCategoryError(null);
    setCategoryTagFilter(null);
    setSelectedCategoryId(id);
  };

  const closeDrawer = () => {
    setSelectedCategoryId(null);
    setCategoryDetail(null);
    setCategoryError(null);
    setCategoryTagFilter(null);
  };

  // ── Derived data for chart ──
  const colorMap = useMemo(() => {
    if (!topics?.categories) return {};
    const sorted = [...topics.categories].sort(
      (a, b) => (b.total || 0) - (a.total || 0),
    );
    const map = {};
    sorted.forEach((c, i) => {
      map[c.categoryId] = CATEGORY_PALETTE[i % CATEGORY_PALETTE.length];
    });
    return map;
  }, [topics]);

  const topCategoryIds = useMemo(
    () => (topics?.categories ? pickTopCategoryIds(topics.categories, 6) : []),
    [topics],
  );

  const idToName = useMemo(() => {
    const m = {};
    (topics?.categories || []).forEach((c) => {
      m[c.categoryId] = c.name;
    });
    return m;
  }, [topics]);

  const chartRows = useMemo(() => {
    if (!topics) return [];
    const series = {};
    for (const id of topCategoryIds) {
      const cat = topics.categories.find((c) => c.categoryId === id);
      series[id] = cat?.countsByBucket || [];
    }
    return seriesToRechartsRows(topics.buckets, series);
  }, [topics, topCategoryIds]);

  const tableCategories = useMemo(() => {
    if (!topics?.categories) return [];
    return [...topics.categories].sort(
      (a, b) => (b.totalInWindow || 0) - (a.totalInWindow || 0),
    );
  }, [topics]);

  const totalActionableInWindow = topics?.totalTicketsInWindow || 0;

  // ── Styles ──
  const dividerColor = isDark ? "#2d3148" : "#e2e8f0";
  const cardStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${dividerColor}`,
    borderRadius: 12,
    padding: "20px 22px",
    marginBottom: 20,
    boxShadow: isDark
      ? "0 4px 24px rgba(0,0,0,0.3)"
      : "0 2px 12px rgba(0,0,0,0.04)",
  };

  const segBtnStyle = (active) => ({
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    background: active ? ACCENT : isDark ? "#2d3148" : "#e2e8f0",
    color: active ? "#fff" : isDark ? "#a0aec0" : "#4a5568",
    whiteSpace: "nowrap",
  });

  const kpis = overview?.kpis;

  return (
    <div
      className="container-fluid SupportInsightsPageContainer"
      style={{
        color: isDark ? "#e2e8f0" : "#1a202c",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={4000}
        theme={isDark ? "dark" : "light"}
      />

      <div style={{ padding: "20px 24px 48px" }}>
        {/* ── Header ── */}
        <div
          style={{
            marginBottom: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <FaHeadset style={{ color: ACCENT }} />
              Support Insights
            </h1>
            <p
              style={{
                margin: "6px 0 0",
                color: isDark ? "#a0aec0" : "#6b7280",
                fontSize: 14,
              }}
            >
              Trends and patterns across all Jira SUP tickets, classified by AI into{" "}
              {taxonomy?.categories?.length || 11} operational categories.
              {taxonomy?.version && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: isDark ? "#1e2130" : "#f7f9fc",
                    border: `1px solid ${dividerColor}`,
                  }}
                >
                  taxonomy {taxonomy.version}
                </span>
              )}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4, padding: 4, background: isDark ? "#1e2130" : "#edf2f7", borderRadius: 8 }}>
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.days}
                  type="button"
                  onClick={() => setDays(opt.days)}
                  style={segBtnStyle(days === opt.days)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 4, padding: 4, background: isDark ? "#1e2130" : "#edf2f7", borderRadius: 8 }}>
              <button
                type="button"
                onClick={() => setBucket("weekly")}
                style={segBtnStyle(bucket === "weekly")}
              >
                Weekly
              </button>
              <button
                type="button"
                onClick={() => setBucket("monthly")}
                style={segBtnStyle(bucket === "monthly")}
              >
                Monthly
              </button>
            </div>
            <button
              type="button"
              onClick={loadDashboard}
              disabled={isLoading}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                border: `1px solid ${dividerColor}`,
                background: isDark ? "#1e2130" : "#fff",
                color: isDark ? "#e2e8f0" : "#1a202c",
                borderRadius: 8,
                cursor: isLoading ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FaSyncAlt style={isLoading ? { animation: "spin 1s linear infinite" } : {}} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              background: isDark ? "#3a1d20" : "#fff5f5",
              border: `1px solid ${isDark ? "#e76767" : "#feb2b2"}`,
              color: isDark ? "#feb2b2" : "#c53030",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            <FaExclamationTriangle style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {/* ── In-page detail view (shown when a category is selected) ── */}
        {selectedCategoryId && (
          <CategoryDetailView
            detail={categoryDetail}
            loading={categoryLoading}
            error={categoryError}
            bucket={bucket}
            isDark={isDark}
            tagFilter={categoryTagFilter}
            setTagFilter={setCategoryTagFilter}
            onLoadMore={() =>
              loadCategory(selectedCategoryId, { append: true, tag: categoryTagFilter })
            }
            loadingMore={categoryLoadingMore}
            categoryColor={colorMap[selectedCategoryId] || ACCENT}
            onBack={closeDrawer}
            cardStyle={cardStyle}
          />
        )}

        {/* ── Dashboard view (hidden while a category is selected) ── */}
        {!selectedCategoryId && (
        <>

        {/* ── KPI strip ── */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            marginBottom: 20,
          }}
        >
          <KpiCard
            label="Total tickets"
            value={kpis?.totalTickets ?? "—"}
            sublabel={
              kpis
                ? `${kpis.classifiedTickets} classified · ${kpis.failedClassification} failed`
                : ""
            }
            isDark={isDark}
          />
          <KpiCard
            label={`In last ${days} days`}
            value={kpis?.ticketsInWindow ?? "—"}
            sublabel={
              kpis
                ? `${kpis.actionableInWindow} actionable`
                : ""
            }
            isDark={isDark}
          />
          <KpiCard
            label="Top category in window"
            value={overview?.topCategoriesInWindow?.[0]?.name || "—"}
            sublabel={
              overview?.topCategoriesInWindow?.[0]
                ? `${overview.topCategoriesInWindow[0].count} tickets · ${overview.topCategoriesInWindow[0].pct}%`
                : ""
            }
            isDark={isDark}
          />
          <KpiCard
            label="Top tag in window"
            value={
              overview?.topTagsInWindow?.[0]
                ? formatTag(overview.topTagsInWindow[0].tag).label
                : "—"
            }
            sublabel={
              overview?.topTagsInWindow?.[0]
                ? `${formatTag(overview.topTagsInWindow[0].tag).namespaceLabel} · ${overview.topTagsInWindow[0].count} tickets`
                : ""
            }
            isDark={isDark}
          />
          <KpiCard
            label="Noise / hygiene"
            value={kpis?.noiseTickets ?? "—"}
            sublabel="Excluded from trend"
            accent={isDark ? "#a0aec0" : "#94a3b8"}
            isDark={isDark}
          />
        </div>

        {/* ── Trend chart ── */}
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 12,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: isDark ? "#a0aec0" : "#6b7280",
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Volume by category · {bucket}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                Top 6 categories over the last {days} days
              </div>
            </div>
          </div>
          {isLoading && !topics ? (
            <ChartSkeleton isDark={isDark} />
          ) : chartRows.length === 0 ? (
            <EmptyChart isDark={isDark} />
          ) : (
            <TrendChart
              rows={chartRows}
              categoryIds={topCategoryIds}
              idToName={idToName}
              colorMap={colorMap}
              bucket={bucket}
              isDark={isDark}
            />
          )}
        </div>

        {/* ── Top tags strip (in window) ── */}
        {overview?.topTagsInWindow?.length > 0 && (
          <div style={cardStyle}>
            <SectionHeader title="Top tags in window" isDark={isDark}>
              <span style={{ fontSize: 12, color: isDark ? "#a0aec0" : "#6b7280" }}>
                <FaTags style={{ marginRight: 6 }} />
                Tags are explicit mentions in ticket text. Click a category for filtered drill-down.
              </span>
            </SectionHeader>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {overview.topTagsInWindow.slice(0, 18).map((t) => (
                <TagPill
                  key={t.tag}
                  tag={t.tag}
                  count={t.count}
                  isDark={isDark}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Topics table ── */}
        <div style={cardStyle}>
          <SectionHeader title="Categories" isDark={isDark}>
            <span style={{ fontSize: 12, color: isDark ? "#a0aec0" : "#6b7280" }}>
              <FaFilter style={{ marginRight: 6 }} />
              Click a row to drill in
            </span>
          </SectionHeader>
          {isLoading && !topics ? (
            <TableSkeleton isDark={isDark} />
          ) : tableCategories.length === 0 ? (
            <div
              style={{
                padding: "32px",
                textAlign: "center",
                color: isDark ? "#a0aec0" : "#6b7280",
                fontSize: 14,
              }}
            >
              No categorized tickets in this window.
            </div>
          ) : (
            <TopicsTable
              categories={tableCategories}
              totalActionable={totalActionableInWindow}
              selectedId={selectedCategoryId}
              onSelect={handleSelectCategory}
              isDark={isDark}
              colorMap={colorMap}
            />
          )}
        </div>
        </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes supportFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const ChartSkeleton = ({ isDark }) => (
  <div
    style={{
      width: "100%",
      height: 320,
      borderRadius: 8,
      background: isDark ? "#1e2130" : "#f7f9fc",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? "#a0aec0" : "#6b7280",
      fontSize: 13,
    }}
  >
    <FaSpinner style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />
    Loading trend…
  </div>
);

const EmptyChart = ({ isDark }) => (
  <div
    style={{
      width: "100%",
      height: 240,
      borderRadius: 8,
      background: isDark ? "#1e2130" : "#f7f9fc",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? "#a0aec0" : "#6b7280",
      fontSize: 13,
    }}
  >
    No data in this window yet.
  </div>
);

const TableSkeleton = ({ isDark }) => (
  <div
    style={{
      padding: "32px",
      textAlign: "center",
      color: isDark ? "#a0aec0" : "#6b7280",
      fontSize: 13,
    }}
  >
    <FaSpinner style={{ animation: "spin 1s linear infinite", marginRight: 8 }} />
    Loading categories…
  </div>
);

export default SupportInsightsPage;
